import React, { lazy, Suspense, useMemo, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import Footer from './components/Footer';
import { getPerformancePreferences } from './utils/performancePreferences';

const Home = lazy(() => import('./Home'));
const ProjectDetail = lazy(() => import('./page/ProjectDetail'));
const Certificates = lazy(() => import('./pages/Certificates'));
const Why927 = lazy(() => import('./pages/Why927'));
const AnimatedCursor = lazy(() => import('./components/AnimatedCursor'));
const ContactWidget = lazy(() => import('./components/ContactWidget'));
const ChatHere = lazy(() => import('./components/ChatHere'));
const BackgroundVideo = lazy(() => import('./components/BackgroundVideo'));
const Disable = lazy(() => import('./components/Disable'));

const AppFallback = () => (
  <div className="min-h-screen bg-white dark:bg-gray-950" aria-label="Loading page" />
);

const useDeferredGlobalUi = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(() => setReady(true), { timeout: 1800 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timer = window.setTimeout(() => setReady(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  return ready;
};

// Move location logic to a child component
const AppContent = ({ isDarkMode, toggleTheme }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const showDeferredUi = useDeferredGlobalUi();
  const performancePreferences = useMemo(() => getPerformancePreferences(), []);
  const showEffects = showDeferredUi && !performancePreferences.reduceEffects;

  return (
    <>
      <Suspense fallback={null}>
        {showDeferredUi && <BackgroundVideo performancePreferences={performancePreferences} />}
      </Suspense>
      {!isHomePage && <Header toggleTheme={toggleTheme} isDarkMode={isDarkMode} className='fixed' />}
      <Suspense fallback={null}>
        {showEffects && <AnimatedCursor performancePreferences={performancePreferences} />}
      </Suspense>
      <ScrollToTop />
      <main>
        <Suspense fallback={<AppFallback />}>
          <Routes>
            <Route path="/" element={<Home isDarkMode={isDarkMode} toggleTheme={toggleTheme} />} />
            <Route path="/project/:projectId" element={<ProjectDetail isDarkMode={isDarkMode} toggleTheme={toggleTheme} />} />
            <Route path="/certificates" element={<Certificates isDarkMode={isDarkMode} toggleTheme={toggleTheme} />} />
            <Route path="/why-927" element={<Why927 />} />
          </Routes>
        </Suspense>
        <Footer />
      </main>
      <Suspense fallback={null}>
        {showDeferredUi && (
          <>
            <ChatHere />
            <ContactWidget />
          </>
        )}
      </Suspense>
    </>
  );
};

const App = () => {
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <BrowserRouter>
        <AppContent isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
      </BrowserRouter>
      <Suspense fallback={null}>
        <Disable />
      </Suspense>
    </div>
  );
};

export default App;
